import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@web-chat-core/ui-kit';
import type { Participant, Room } from 'livekit-client';
import { roomInfoStore } from 'state/store';
import { selectLocalParticipant, selectRoomId } from 'state/selectors';
import { MODERATION_ACTION_NAMES, MODERATOR_ACTIONS } from 'useCase/moderation/constants';
import { publishModerationCommand } from 'useCase/moderation/commands/publishCommand';
import { isGuestParticipant } from 'useCase/moderation/status/moderators';
import { getRoomNameAndWorkspaceId, removeModeratorFromCallApi } from 'useCase/moderation/utils/moderatorApi';
import { requireParticipantSid } from 'useCase/moderation/utils/validateParticipantSid';
import { getNameFromMetadata, getParticipantUserIdFromMetadata } from 'useCase/participant/useParticipantMetadata';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import { getSpeakerIdentityForApi, getWebinarMode, matchesWebinarSpeaker } from 'useCase/webinar/webinarStatus';
import { assignSpeakerToCallApi, removeSpeakerFromCallApi } from 'useCase/webinar/speakerApi';
import { getConferenceJwtToken } from 'features/AccessRequests/utils/getConferenceJwtToken';
import type { ModerationConfirmState } from '../types';

interface UseModerationActionsProps {
    targetParticipant: Participant;
    room: Room | null;
    setConfirm: (confirm: ModerationConfirmState) => void;
}

/**
 * Хук для действий модерации (mute, disable video, kick и тд)
 */
export const useModerationActions = ({ targetParticipant, room, setConfirm }: UseModerationActionsProps) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const { getTokenAndWssUrl } = useConnectToRoom();

    const targetName = useMemo(
        () => getNameFromMetadata(targetParticipant.metadata, t('web_call_unnamed_user')),
        [targetParticipant.metadata, t],
    );

    const requireSidOrNotify = useCallback(
        (actionName: string): string | null => {
            const sid = requireParticipantSid(targetParticipant, actionName);
            if (!sid) {
                showSnackbar(t('web_videoconference_error'));
            }
            return sid;
        },
        [targetParticipant, showSnackbar, t],
    );

    const muteMic = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.MUTE_MIC);
        if (!sid) return;
        try {
            const published = publishModerationCommand(room, MODERATOR_ACTIONS.MUTE, {
                sid,
                identity: targetParticipant.identity,
            });
            if (!published) showSnackbar(t('web_videoconference_error'));
        } catch {
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, requireSidOrNotify]);

    const muteCam = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.MUTE_CAM);
        if (!sid) return;
        try {
            const published = publishModerationCommand(room, MODERATOR_ACTIONS.DISABLE_VIDEO, {
                sid,
                identity: targetParticipant.identity,
            });
            if (!published) showSnackbar(t('web_videoconference_error'));
        } catch {
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, requireSidOrNotify]);

    const openMuteMicOthersConfirm = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.OPEN_MUTE_MIC_OTHERS_CONFIRM);
        if (!sid) return;
        setConfirm({ kind: 'MUTE_OTHERS_AUDIO', targetSid: sid, targetName });
    }, [targetParticipant, targetName, setConfirm, requireSidOrNotify]);

    const openMuteCamOthersConfirm = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.OPEN_MUTE_CAM_OTHERS_CONFIRM);
        if (!sid) return;
        setConfirm({ kind: 'MUTE_OTHERS_VIDEO', targetSid: sid, targetName });
    }, [targetParticipant, targetName, setConfirm, requireSidOrNotify]);

    const openKickConfirm = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.OPEN_KICK_CONFIRM);
        if (!sid) return;
        setConfirm({ kind: 'KICK', targetSid: sid, targetName });
    }, [targetParticipant, targetName, setConfirm, requireSidOrNotify]);

    const openMuteOthersFromLocalTileMicConfirm = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.OPEN_MUTE_OTHERS_FROM_LOCAL_TILE_MIC_CONFIRM);
        if (!sid) return;
        const localName = t('web_call_you_started');
        setConfirm({ kind: 'MUTE_OTHERS_AUDIO', targetSid: sid, targetName: localName });
    }, [targetParticipant, t, setConfirm, requireSidOrNotify]);

    const openMuteOthersFromLocalTileCamConfirm = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.OPEN_MUTE_OTHERS_FROM_LOCAL_TILE_CAM_CONFIRM);
        if (!sid) return;
        const localName = t('web_call_you_started');
        setConfirm({ kind: 'MUTE_OTHERS_VIDEO', targetSid: sid, targetName: localName });
    }, [targetParticipant, t, setConfirm, requireSidOrNotify]);

    const inviteToSpeak = useCallback(() => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.INVITE_TO_SPEAK);
        if (!sid) return;
        try {
            const published = publishModerationCommand(room, MODERATOR_ACTIONS.INVITE_TO_SPEAK, {
                sid,
                identity: targetParticipant.identity,
            });
            if (!published) showSnackbar(t('web_videoconference_error'));
        } catch {
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, requireSidOrNotify]);

    const openAssignModeratorConfirm = useCallback(() => {
        const sid = requireSidOrNotify('назначить модератором');
        if (!sid) return;
        setConfirm({ kind: 'MAKE_MODERATOR', targetSid: sid, targetName });
    }, [targetParticipant, targetName, setConfirm, requireSidOrNotify]);

    const revokeModerator = useCallback(async () => {
        const sid = requireSidOrNotify('отозвать роль модератора');
        if (!sid) return;
        try {
            const published = publishModerationCommand(room, MODERATOR_ACTIONS.REVOKE_MODERATOR, {
                sid,
                identity: targetParticipant.identity,
            });
            if (!published) {
                showSnackbar(t('web_videoconference_error'));
                return;
            }
            const targetUserId = getParticipantUserIdFromMetadata(targetParticipant.metadata);
            const targetIdentity = targetParticipant.identity ?? null;

            const { roomName, workspaceId } = getRoomNameAndWorkspaceId();
            if (!roomName) {
                console.warn('[Moderation] removeModeratorFromCall не отправлен: нет roomName.');
                showSnackbar(t('web_videoconference_error'));
                return;
            }
            const localParticipant = selectLocalParticipant();
            const tokenResult = await getTokenAndWssUrl(
                selectRoomId(),
                localParticipant?.name ?? localParticipant?.identity ?? '',
            );
            const payload = tokenResult?.payload;
            const jwtToken = payload && 'jwtToken' in payload ? payload.jwtToken ?? null : null;
            const isLocalGuest = localParticipant != null && isGuestParticipant(localParticipant);
            const result = await removeModeratorFromCallApi({
                roomName,
                workspaceId,
                targetIdentity,
                targetUserId,
                jwtToken,
                isLocalGuest,
            });
            if (result.success) {
                roomInfoStore.removeModeratorFromMetadata(targetUserId, targetIdentity);
            } else {
                showSnackbar(t('web_videoconference_error'));
            }
        } catch {
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, getTokenAndWssUrl, requireSidOrNotify]);

    const assignSpeaker = useCallback(async () => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.MAKE_SPEAKER);
        if (!sid) return;

        const currentWebinarMode = getWebinarMode() ?? {};
        const speaker = getSpeakerIdentityForApi(targetParticipant);
        const speakers = Array.isArray(currentWebinarMode.speakers) ? [...currentWebinarMode.speakers] : [];
        const alreadySpeaker = speakers.some((s) => matchesWebinarSpeaker(s, speaker));
        // Уже спикер — не отправляем дубль и не трогаем метаданные (иначе откат при ошибке удалит реального спикера)
        if (alreadySpeaker) return;

        const published = publishModerationCommand(room, MODERATOR_ACTIONS.MAKE_SPEAKER, {
            sid,
            identity: targetParticipant.identity,
        });
        if (!published) {
            showSnackbar(t('web_videoconference_error'));
            return;
        }

        roomInfoStore.addSpeakerToWebinarMetadata(speaker);

        try {
            const { roomName, workspaceId } = getRoomNameAndWorkspaceId();
            if (!roomName) {
                roomInfoStore.removeSpeakerFromWebinarMetadata(speaker);
                showSnackbar(t('web_videoconference_error'));
                return;
            }

            const localParticipant = selectLocalParticipant();
            const isLocalGuest = localParticipant != null && isGuestParticipant(localParticipant);
            const jwtToken = await getConferenceJwtToken(getTokenAndWssUrl);
            const result = await assignSpeakerToCallApi({
                roomName,
                workspaceId,
                targetIdentity: targetParticipant.identity ?? null,
                targetUserId: getParticipantUserIdFromMetadata(targetParticipant.metadata),
                jwtToken,
                isLocalGuest,
            });
            if (!result.success) {
                roomInfoStore.removeSpeakerFromWebinarMetadata(speaker);
                showSnackbar(t('web_videoconference_error'));
            }
        } catch {
            roomInfoStore.removeSpeakerFromWebinarMetadata(speaker);
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, getTokenAndWssUrl, requireSidOrNotify]);

    const revokeSpeaker = useCallback(async () => {
        const sid = requireSidOrNotify(MODERATION_ACTION_NAMES.REVOKE_SPEAKER);
        if (!sid) return;

        const currentWebinarMode = getWebinarMode() ?? {};
        const speaker = getSpeakerIdentityForApi(targetParticipant);
        const speakers = Array.isArray(currentWebinarMode.speakers) ? currentWebinarMode.speakers : [];
        const nextSpeakers = speakers.filter((s) => !matchesWebinarSpeaker(s, speaker));
        // Не был спикером — нечего отзывать (иначе откат при ошибке добавит того, кого не было)
        if (nextSpeakers.length === speakers.length) return;

        roomInfoStore.removeSpeakerFromWebinarMetadata(speaker);

        try {
            const { roomName, workspaceId } = getRoomNameAndWorkspaceId();
            if (!roomName) {
                roomInfoStore.addSpeakerToWebinarMetadata(speaker);
                showSnackbar(t('web_videoconference_error'));
                return;
            }

            const localParticipant = selectLocalParticipant();
            const isLocalGuest = localParticipant != null && isGuestParticipant(localParticipant);
            const jwtToken = await getConferenceJwtToken(getTokenAndWssUrl);
            const result = await removeSpeakerFromCallApi({
                roomName,
                workspaceId,
                targetIdentity: targetParticipant.identity ?? null,
                targetUserId: getParticipantUserIdFromMetadata(targetParticipant.metadata),
                jwtToken,
                isLocalGuest,
            });
            if (!result.success) {
                roomInfoStore.addSpeakerToWebinarMetadata(speaker);
                showSnackbar(t('web_videoconference_error'));
                return;
            }
            publishModerationCommand(room, MODERATOR_ACTIONS.REVOKE_SPEAKER, {
                sid,
                identity: targetParticipant.identity,
            });
        } catch {
            roomInfoStore.addSpeakerToWebinarMetadata(speaker);
            showSnackbar(t('web_videoconference_error'));
        }
    }, [room, targetParticipant, showSnackbar, t, getTokenAndWssUrl, requireSidOrNotify]);

    return {
        muteMic,
        muteCam,
        inviteToSpeak,
        openMuteMicOthersConfirm,
        openMuteCamOthersConfirm,
        openKickConfirm,
        openMuteOthersFromLocalTileMicConfirm,
        openMuteOthersFromLocalTileCamConfirm,
        openAssignModeratorConfirm,
        revokeModerator,
        assignSpeaker,
        revokeSpeaker,
    };
};
